import React from 'react'

import { AcornsIcon } from '@acorns/icons'
import { useRemoteStrings } from '@acorns/web-utils'
import { useQuery } from '@apollo/react-hooks'

import { TestIDs } from 'test/utils/testIds'

import SuitabilityResponses from '../suitabilityResponses.gql'
import {
  AboutIcon,
  ContentBodyText,
  ContentContainer,
  ContentTitle,
  Footer,
  RecommendedPortfolioTitle,
  StyledInvestListContainer,
  StyledInvestListItem,
  SubContentTitle,
} from './styled-components'

type Props = {
  product: string
  theme: string
  recommendedRisk: string
  investType: string
  laterType: string
}

const AboutPortfolioContent = (props: Props) => {
  const { getRemoteString, StyledRemoteString } = useRemoteStrings()
  const { data: suitabilityResponsesData } = useQuery(SuitabilityResponses, {
    fetchPolicy: 'cache-and-network',
  })

  return (
    <>
      <ContentContainer>
        <AboutIcon icon={AcornsIcon.Icon.DefinePortfolio} width="315px" height="150px" />
        <ContentTitle fontSize="30px" lineHeight="41px">
          {getRemoteString({
            key: 'portfolio.about.title1',
          })}
        </ContentTitle>
        <ContentBodyText>
          {getRemoteString({
            key: `portfolio.about.title1.description.${props.theme}`,
          })}
        </ContentBodyText>
      </ContentContainer>
      <ContentContainer>
        <ContentTitle>
          {getRemoteString({
            key: 'portfolio.about.stock_market.title',
          })}
        </ContentTitle>
        <ContentBodyText marginBottom="20px">
          {getRemoteString({
            key: 'portfolio.about.stock_market.description1',
          })}
        </ContentBodyText>
        <ContentBodyText>
          {getRemoteString({
            key: 'portfolio.about.stock_market.description2',
          })}
        </ContentBodyText>
      </ContentContainer>
      <ContentContainer>
        <ContentTitle>
          {getRemoteString({
            key: 'portfolio.about.acorns_market.title',
          })}
        </ContentTitle>
        <ContentBodyText>
          {getRemoteString({
            key: 'portfolio.about.acorns_market.description',
          })}
        </ContentBodyText>
      </ContentContainer>
      <ContentContainer>
        <ContentTitle>
          {getRemoteString({
            key: 'portfolio.about.title2',
          })}
        </ContentTitle>
        <ContentBodyText>
          <StyledRemoteString
            stringKey={'portfolio.about.title2.description'}
            modifiers={[{ bold: 600 }, { bold: 600 }]}
          />
        </ContentBodyText>
      </ContentContainer>
      <ContentContainer>
        <ContentTitle>
          {getRemoteString({
            key: 'portfolio.about.title3',
          })}
        </ContentTitle>
        <SubContentTitle>
          {getRemoteString({
            key: `portfolio.about.title4.${props.theme}`,
          })}
        </SubContentTitle>
        <ContentBodyText marginBottom="20px">
          {getRemoteString({
            key: `portfolio.about.title4.description.${props.theme}`,
          })}
        </ContentBodyText>
        {props.theme === 'sustainable' && (
          <ContentBodyText marginBottom="10px">
            {getRemoteString({
              key: 'portfolio.about.title4.description2.sustainable',
            })}
          </ContentBodyText>
        )}
        <SubContentTitle>
          {getRemoteString({
            key: 'portfolio.about.title5',
          })}
        </SubContentTitle>
        <ContentBodyText>
          {getRemoteString({
            key: `portfolio.about.title5.description.${props.theme}`,
          })}
        </ContentBodyText>
      </ContentContainer>
      <ContentContainer>
        <RecommendedPortfolioTitle>
          {getRemoteString({
            key: 'portfolio.about.title6',
          })}
        </RecommendedPortfolioTitle>
        <ContentTitle data-testid={TestIDs.PREVIEW_PORTFOLIO.PORTFOLIO_RECOMMENDED_RISK}>
          {props.recommendedRisk}
        </ContentTitle>
        {props.product === props.laterType ? (
          <ContentBodyText>
            {getRemoteString({
              key: 'portfolio.about.title6.description.later.variable',
              variables: [props.recommendedRisk],
            })}
          </ContentBodyText>
        ) : (
          <ContentBodyText marginBottom="20px">
            {props.product === props.investType &&
              getRemoteString({
                key: `portfolio.about.title6.description.${props.recommendedRisk
                  .toLowerCase()
                  .replace(' ', '_')}.invest`,
              })}
          </ContentBodyText>
        )}
        {props.product === props.investType && (
          <>
            <ContentBodyText marginBottom="20px">
              {getRemoteString({
                key: 'portfolio.about.title6.description2',
              })}
            </ContentBodyText>
            <StyledInvestListContainer>
              {suitabilityResponsesData?.suitabilityResponses?.map(
                (response: { answer: { summary: string } }, index: number) => {
                  return (
                    <StyledInvestListItem key={index}>
                      {response?.answer?.summary}
                    </StyledInvestListItem>
                  )
                },
              )}
            </StyledInvestListContainer>
          </>
        )}
      </ContentContainer>
      <ContentContainer marginBottom="0px">
        <Footer>
          {getRemoteString({
            key: 'portfolio.about.footer1',
          })}
        </Footer>
        <Footer>
          {getRemoteString({
            key: 'portfolio.about.footer2',
          })}
        </Footer>
        <Footer>
          {getRemoteString({
            key: 'portfolio.about.footer3',
          })}
        </Footer>
      </ContentContainer>
    </>
  )
}

export default AboutPortfolioContent
